/**
 * EhrHeading - the H2 of each /ehr section, in the homepage's section
 * headline type (FaqSection, PayerWallSection): Space Grotesk with an
 * Instrument Serif italic beat, on its own line or closing the first.
 *
 * The space before the beat stays in the markup even when the beat takes its
 * own line, so the heading's text reads as two words, not one, to crawlers
 * and screen readers.
 */

import type { ReactNode } from "react";
import { useReveal } from "@/components/landing/landing-animations";

interface EhrHeadingProps {
  lead: ReactNode;
  beat: ReactNode;
  /** The beat on its own line, as on FaqSection, instead of closing the first. */
  ownLine?: boolean;
  align?: "center" | "left";
  beatColor?: string;
  marginBottom: number;
}

export function EhrHeading({
  lead,
  beat,
  ownLine = false,
  align = "center",
  beatColor,
  marginBottom,
}: EhrHeadingProps) {
  const ref = useReveal<HTMLHeadingElement>();
  return (
    <h2
      ref={ref}
      className="landing-reveal"
      style={{
        margin: `0 0 ${marginBottom}px`,
        textAlign: align,
        fontFamily: "'Space Grotesk',sans-serif",
        fontSize: "clamp(32px,4.6vw,54px)",
        fontWeight: 600,
        letterSpacing: "-0.025em",
        lineHeight: 1.08,
        maxWidth: 880,
        textWrap: "balance",
      }}
    >
      {lead}{" "}
      <span
        style={{
          ...(ownLine ? { display: "block", marginTop: "0.06em" } : null),
          fontFamily: "'Instrument Serif',Georgia,serif",
          fontStyle: "italic",
          fontWeight: 400,
          color: beatColor,
        }}
      >
        {beat}
      </span>
    </h2>
  );
}
